import { useState } from "react";
import { ABOUT_NOT_ADMIN, EMPTY_NOT_ADMIN, SYNC_ADMIN_ONLY } from "../lib/accounts-copy.ts";
import type { Standing, TikTokAccount } from "../lib/api.ts";
import { api } from "../lib/api.ts";
import { accountStatus } from "../lib/format.ts";
import { isPlatformAdmin } from "../lib/roles.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Badge, Button, InfoBanner, Page, Table } from "../ui/index.ts";
import { badgeVariant } from "./Overview.tsx";

export function Accounts({ standing }: { standing: Standing }) {
	const [accounts, reload] = useLoad(() => api.tiktokAccounts());
	const [busy, setBusy] = useState(false);
	// A failed sync and a successful one are not the same news. Both used to land
	// in one `note` rendered as an info banner, so an upstream "token revoked"
	// error arrived looking exactly like "Pulled 4 accounts".
	const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);

	async function sync() {
		setBusy(true);
		setNote(null);
		try {
			const { synced } = await api.syncTikTokAccounts();
			setNote({
				text: `Pulled ${synced} account${synced === 1 ? "" : "s"} from TikTok.`,
				failed: false,
			});
			reload();
		} catch (e) {
			setNote({ text: e instanceof Error ? e.message : String(e), failed: true });
		} finally {
			setBusy(false);
		}
	}

	const list = accounts.state === "ready" ? accounts.data.accounts : [];

	return (
		<Page
			title="Ad accounts"
			description={
				isPlatformAdmin(standing)
					? "Read from this console's own database, not from TikTok on every load. Pull a refresh when the advertisers this console can reach change."
					: ABOUT_NOT_ADMIN
			}
			actions={
				isPlatformAdmin(standing) ? (
					<Button onClick={() => void sync()} loading={busy}>
						Sync from TikTok
					</Button>
				) : (
					<p className="max-w-sm text-sm text-muted">{SYNC_ADMIN_ONLY}</p>
				)
			}
		>
			{note && <InfoBanner variant={note.failed ? "danger" : "info"} title={note.text} />}
			{accounts.state === "ready" && accounts.data.truncated && (
				<InfoBanner variant="warning" title="This list is not complete.">
					More rows exist than one read returns, so what you see here is the first 1000. Nothing is
					missing from the database — the list on this screen is short, and it will stay short until
					paging is added.
				</InfoBanner>
			)}
			{accounts.state === "failed" && (
				<InfoBanner variant="danger" title="Accounts could not be read.">
					{accounts.why}
				</InfoBanner>
			)}

			<Table<TikTokAccount>
				columns={[
					{
						id: "name",
						header: "Account",
						accessor: (a) => (
							<>
								<div className="font-medium text-content">{a.name}</div>
								<div className="font-mono text-xs text-muted">{a.id}</div>
							</>
						),
					},
					{
						id: "advertiser",
						header: "Advertiser ID",
						accessor: (a) => <span className="font-mono text-xs">{a.advertiserId}</span>,
					},
					{ id: "currency", header: "Currency", accessor: (a) => a.currency ?? "—" },
					{
						id: "state",
						header: "State",
						accessor: (a) => {
							const st = accountStatus(a.advertiserStatus);
							return <Badge variant={badgeVariant(st.tone)}>{st.label}</Badge>;
						},
					},
					{
						id: "synced",
						header: "Synced",
						accessor: (a) => (
							<span className="text-muted">
								{a.syncedAt ? new Date(a.syncedAt).toLocaleString() : "never"}
							</span>
						),
					},
				]}
				data={list}
				keyExtractor={(a) => a.id}
				failed={accounts.state === "failed"}
				loading={accounts.state === "loading"}
				emptyMessage={
					isPlatformAdmin(standing)
						? "Nothing synced yet. Once the TikTok app is approved and the access token configured, pull a refresh and the advertisers it can reach appear here."
						: EMPTY_NOT_ADMIN
				}
			/>
		</Page>
	);
}
